<template>
  <section class="container-full-5">
    <h2 class="secondary-font gap-4 mt-2 mb-6">Utilisation</h2>

    <!-- Stats Décaissement / Utilisation -->
    <div class="grid2 mb-5">
      <div class="mb-4">
        <h1 class="secondary-font font-semibold text-5xl text-grey-900 my-2">{{ formatAmount(totalDecaissement) }} F</h1>
        <h3 class="flex gap-3 secondary-font font-semibold text-grey-700">
          <div class="circle-medium bg-color-1000"></div>
          Décaissement
        </h3>
      </div>
      <div class="mb-4">
        <h1 class="secondary-font font-semibold text-5xl text-grey-900 my-2">{{ formatAmount(totalUtilisation) }} F</h1>
        <h3 class="flex gap-3 secondary-font font-semibold text-grey-700">
          <div class="circle-medium bg-purple"></div>
          Utilisation
        </h3>
      </div>
    </div>

    <!-- Graphique Décaissement vs Utilisation -->
    <div class="s20 mb-6" v-if="moisList.length > 0">
      <Line :data="lineChartData" :options="lineChartOptions" />
    </div>

    <!-- Filtre par mois -->
    <div v-if="moisList.length > 0" class="s20 flex gap-3 mb-6">
      <span
        v-for="(m, idx) in moisList"
        :key="idx"
        class="btn sm cursor-pointer"
        :class="selectedMoisIndex === idx ? 'light2' : 'light'"
        @click="selectMois(idx)"
      >
        {{ moisLabel(m.mois) }}
      </span>
    </div>

    <!-- Liste de Transactions -->
    <div class="s20">
      <div
        v-for="tx in currentTransactions"
        :key="tx.id"
        class="flex gap-6 mb-4 cursor-pointer"
        @click="goToTransaction(tx.id)"
      >
        <div class="square-xl px-4 py-4" style="background-color: rgba(0, 185, 172, 0.08);">
          <img src="/public/icons/icon-trans-recharger.svg" width="100%" class="icon-neutral" alt="" />
        </div>
        <div class="s13 mt-2">
          <h6 class="secondary-font font-semibold mb-1">{{ tx.libelle }}</h6>
          <p class="secondary-font font-semibold text-sm text-grey-600 mb-2">
            {{ formatDateTransaction(tx.date) }} - {{ tx.reference }}
          </p>
        </div>
        <h5 class="s5 secondary-font text-right mt-2" :class="tx.type === 'credit' ? 'text-green' : ''">
          {{ tx.type === 'credit' ? '+' : '-' }} {{ formatAmount(tx.montant) }} F
        </h5>
      </div>

      <div v-if="currentTransactions.length === 0 && !loading" class="text-grey-600 mb-4">
        Aucune transaction pour ce mois
      </div>

      <!-- Pagination -->
      <div v-if="pagination.last_page > 1" class="pagination sm justify-content-left mt-5">
        <button
          class="page-link"
          :class="{ active: pagination.current_page > 1 }"
          :disabled="pagination.current_page <= 1"
          @click="changePage(pagination.current_page - 1)"
        >
          <img src="/public/icons/icon-pagination-prev.svg" class="pagination-icon" alt="" />
          Précédent
        </button>
        <button
          v-for="p in pagesArray"
          :key="p"
          class="page-link"
          :class="{ active: p === pagination.current_page }"
          @click="changePage(p)"
        >
          {{ p }}
        </button>
        <button
          class="page-link pl-4"
          :class="{ active: pagination.current_page < pagination.last_page }"
          :disabled="pagination.current_page >= pagination.last_page"
          @click="changePage(pagination.current_page + 1)"
        >
          Suivant
          <img src="/public/icons/icon-pagination-next.svg" class="pagination-icon ml-2" alt="" />
        </button>
      </div>
    </div>

    <div v-if="loading" class="flex-center py-7">
      <p class="secondary-font text-grey-600">Chargement...</p>
    </div>

    <div class="clear-both h-60"></div>

  </section>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { Line } from 'vue-chartjs'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'
import walletService, { type WalletTransaction, type MoisAggregate } from '@/services/walletService'
import { formatAmount } from '@/utils/formatters'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler)

const MOIS_NOMS = [
  '', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
]

export default defineComponent({
  name: 'WalletUtilisation',
  components: { Line },
  setup() {
    const router = useRouter()
    const loading = ref(true)
    const moisList = ref<MoisAggregate[]>([])
    const selectedMoisIndex = ref(0)
    const currentTransactions = ref<WalletTransaction[]>([])
    const pagination = ref({
      current_page: 1,
      last_page: 1,
      total: 0,
      per_page: 20
    })

    const selectedMois = computed(() => moisList.value[selectedMoisIndex.value] || null)

    const totalDecaissement = computed(() => {
      if (!selectedMois.value) return 0
      return selectedMois.value.montant_credit || 0
    })

    const totalUtilisation = computed(() => {
      if (!selectedMois.value) return 0
      return selectedMois.value.montant_debit || 0
    })

    const pagesArray = computed(() => {
      const pages: number[] = []
      for (let i = 1; i <= pagination.value.last_page; i++) {
        pages.push(i)
      }
      return pages
    })

    function moisLabel(mois: number): string {
      return MOIS_NOMS[mois] || ''
    }

    function formatDateTransaction(dateStr: string): string {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      if (isNaN(date.getTime())) return '-'
      const mois = [
        'Jan.', 'Fév.', 'Mars', 'Avr.', 'Mai', 'Juin',
        'Juil.', 'Août', 'Sept.', 'Oct.', 'Nov.', 'Déc.'
      ]
      const jour = date.getDate().toString().padStart(2, '0')
      const moisNom = mois[date.getMonth()]
      const annee = date.getFullYear()
      const heures = date.getHours().toString().padStart(2, '0')
      const minutes = date.getMinutes().toString().padStart(2, '0')
      return `${jour} ${moisNom} ${annee} à ${heures}:${minutes}`
    }

    function goToTransaction(id: number) {
      router.push(`/front/wallet/transaction/${id}`)
    }

    async function loadMoisData() {
      loading.value = true
      try {
        const res = await walletService.getTransactionsMois()
        if (res.data && Array.isArray(res.data)) {
          moisList.value = res.data
          if (moisList.value.length > 0) {
            selectMois(0)
          }
        }
      } catch (e) {
        // silencieux
      } finally {
        loading.value = false
      }
    }

    function selectMois(index: number) {
      selectedMoisIndex.value = index
      const mois = moisList.value[index]
      if (mois) {
        // Utiliser les transactions incluses dans les données mensuelles
        currentTransactions.value = (mois.transactions || []).filter(tx => tx.walletId === null)
        pagination.value = {
          current_page: 1,
          last_page: 1,
          total: currentTransactions.value.length,
          per_page: 20
        }
        // Si les transactions ne sont pas incluses, charger via API paginée
        if (currentTransactions.value.length === 0 && mois.nombre > 0) {
          loadPaginatedTransactions(mois.mois, mois.annee, 1)
        }
      }
    }

    async function loadPaginatedTransactions(mois: number, annee: number, page: number) {
      try {
        const res = await walletService.getTransactions({
          per_page: 20,
          page
        })
        if (res.data) {
          currentTransactions.value = (res.data.data || []).filter(tx => tx.walletId === null)
          pagination.value = {
            current_page: res.data.current_page,
            last_page: res.data.last_page,
            total: res.data.total,
            per_page: res.data.per_page
          }
        }
      } catch (e) {
        // silencieux
      }
    }

    function changePage(page: number) {
      if (page < 1 || page > pagination.value.last_page) return
      const mois = selectedMois.value
      if (mois) {
        loadPaginatedTransactions(mois.mois, mois.annee, page)
      }
    }

    const lineChartData = computed(() => {
      const labels = moisList.value.map(m => MOIS_NOMS[m.mois]?.substring(0, 3) || '').reverse()
      const credits = moisList.value.map(m => m.montant_credit || 0).reverse()
      const debits = moisList.value.map(m => m.montant_debit || 0).reverse()
      return {
        labels,
        datasets: [
          {
            label: 'Décaissement',
            data: credits,
            borderColor: '#00B9AC',
            backgroundColor: 'rgba(0, 185, 172, 0.1)',
            fill: true,
            tension: 0.4,
            pointRadius: 4,
            pointBackgroundColor: '#00B9AC'
          },
          {
            label: 'Utilisation',
            data: debits,
            borderColor: '#D00EF5',
            backgroundColor: 'rgba(208, 14, 245, 0.1)',
            fill: true,
            tension: 0.4,
            pointRadius: 4,
            pointBackgroundColor: '#D00EF5'
          }
        ]
      }
    })

    const lineChartOptions = {
      responsive: true,
      maintainAspectRatio: true,
      interaction: { intersect: false, mode: 'index' as const },
      plugins: {
        legend: {
          display: true,
          position: 'bottom' as const,
          labels: { usePointStyle: true, padding: 20 }
        },
        tooltip: {
          callbacks: {
            label: (ctx: any) => `${ctx.dataset.label}: ${formatAmount(ctx.raw)} F`
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: (value: any) => formatAmount(value) + ' F'
          }
        }
      }
    }

    onMounted(() => {
      loadMoisData()
    })

    return {
      loading,
      moisList,
      selectedMoisIndex,
      selectedMois,
      currentTransactions,
      pagination,
      totalDecaissement,
      totalUtilisation,
      pagesArray,
      formatAmount,
      moisLabel,
      formatDateTransaction,
      goToTransaction,
      selectMois,
      changePage,
      lineChartData,
      lineChartOptions
    }
  }
})
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
